import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Shield,
  Play,
  Clock,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Users,
  Building2,
  Scale,
  TrendingUp,
  Send,
  XCircle,
  Eye,
  MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/cn';

// Custom DryVox Logo - Unique anchor design
const DryVoxLogo = ({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' | 'xl' }) => {
  const sizeClasses = {
    sm: 'w-5 h-5',
    md: 'w-7 h-7',
    lg: 'w-9 h-9',
    xl: 'w-10 h-10'
  };

  return (
    <svg viewBox="0 0 32 32" fill="none" className={sizeClasses[size]}>
      {/* Anchor ring - top */}
      <circle cx="16" cy="6" r="4" stroke="currentColor" strokeWidth="2.5" fill="none"/>

      {/* Main anchor shaft */}
      <path
        d="M16 10V26"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />

      {/* Anchor crossbar */}
      <path
        d="M10 16H22"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />

      {/* Anchor flukes - curved modern style */}
      <path
        d="M8 28C8 22 12 20 16 26C20 20 24 22 24 28"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />

      {/* Sound wave accents - AI/Voice element */}
      <path
        d="M4 14C5.5 12 5.5 10 4 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.6"
      />
      <path
        d="M28 14C26.5 12 26.5 10 28 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.6"
      />
    </svg>
  );
};

// Ops-centric use cases
const useCases = [
  {
    title: 'Laytime & Demurrage Check',
    description: 'Reviews calculation accuracy and flags discrepancies before claim submission.',
    icon: Clock,
    color: 'from-teal-500 to-cyan-600',
  },
  {
    title: 'NOR / SOF Sanity Review',
    description: 'Validates timeline consistency and identifies gaps before NOR acceptance.',
    icon: FileText,
    color: 'from-blue-500 to-indigo-600',
  },
  {
    title: 'Clause Interpretation',
    description: 'Analyzes visible contract text for risk, ambiguity, and potential disputes.',
    icon: Scale,
    color: 'from-violet-500 to-purple-600',
  },
  {
    title: 'Pre-Send Ops Confirmation',
    description: 'Final checkpoint before sending irreversible commercial communications.',
    icon: Send,
    color: 'from-amber-500 to-orange-600',
  },
];

// Beneficiaries under Ops Leadership
const beneficiaries = [
  {
    role: 'Junior Operators',
    benefit: 'Catch mistakes earlier, learn safely',
    icon: Users,
  },
  {
    role: 'Commercial Teams',
    benefit: 'Fewer post-fixture surprises',
    icon: TrendingUp,
  },
  {
    role: 'Claims Teams',
    benefit: 'Cleaner audit trails',
    icon: FileText,
  },
  {
    role: 'Management',
    benefit: 'Reduced key-person risk',
    icon: Building2,
  },
];

// What DryVox will never do
const neverDo = [
  { text: 'Auto-send messages on your behalf', icon: Send },
  { text: 'Claim legal certainty or guarantee outcomes', icon: Scale },
  { text: 'Replace human judgment', icon: Users },
  { text: 'Guess missing data', icon: AlertTriangle },
];

// The moments DryVox exists for
const moments = [
  'Demurrage claims and rebuttals',
  'NOR/SOF timeline check',
  'Laytime calculations',
  'Clause interpretations based on visible text',
  'Operational messages with commercial impact',
];

/**
 * Landing Page - B2B Operational Checkpoint Positioning
 */
export function Landing() {
  return (
    <div className="min-h-screen bg-[#030712] text-white">
      {/* Background - Simplified, professional */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-[#030712] to-[#030712]" />

        {/* Subtle mesh gradient */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: `
              radial-gradient(ellipse 80% 50% at 20% 40%, rgba(56, 189, 248, 0.1) 0%, transparent 50%),
              radial-gradient(ellipse 60% 40% at 70% 60%, rgba(34, 211, 238, 0.08) 0%, transparent 50%)
            `
          }}
        />

        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      {/* Content wrapper */}
      <div className="relative z-10">
        {/* Navigation */}
        <motion.nav
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="flex items-center justify-between p-6 lg:px-12 max-w-7xl mx-auto"
        >
          <Link to="/" className="flex items-center gap-3 group">
            <motion.div
              whileHover={{ rotate: [0, -10, 10, 0] }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-lg shadow-cyan-500/25 text-white">
                <DryVoxLogo size="sm" />
              </div>
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 blur-lg opacity-40 group-hover:opacity-60 transition-opacity" />
            </motion.div>
            <span className="text-2xl font-display font-bold tracking-tight">
              <span className="text-white">Dry</span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-400">Vox</span>
            </span>
          </Link>

          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white">
                Sign in
              </Button>
            </Link>
            <Link to="/register">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={cn(
                  "px-5 py-2.5 rounded-xl font-medium text-sm",
                  "bg-gradient-to-r from-teal-500 to-cyan-500",
                  "hover:from-teal-400 hover:to-cyan-400",
                  "shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40",
                  "transition-all duration-300"
                )}
              >
                Request Pilot
              </motion.button>
            </Link>
          </div>
        </motion.nav>

        {/* ============================================ */}
        {/* HERO SECTION - Story 13.1 */}
        {/* ============================================ */}
        <section className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div>
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 border border-teal-500/20 mb-8"
              >
                <Shield className="w-4 h-4 text-teal-400" />
                <span className="text-sm font-medium text-teal-300">
                  Operational Checkpoint
                </span>
              </motion.div>

              {/* Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-5xl lg:text-6xl font-display font-bold leading-[1.1] tracking-tight"
              >
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white to-slate-300">
                  Before you click send.
                </span>
              </motion.h1>

              {/* Subheadline */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-lg lg:text-xl text-slate-400 mt-6 max-w-xl leading-relaxed"
              >
                DryVox is a voice-driven operational checkpoint that helps dry bulk ops teams catch risk, gaps, and uncertainties before irreversible actions — with confidence levels and clear audit trails.
              </motion.p>

              {/* Explicit buyer role */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                className="text-base text-teal-400/80 mt-3 font-medium"
              >
                Built for Operations Managers and Post-Fixture Leads in dry bulk shipping.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-wrap items-center gap-4 mt-10"
              >
                <Link to="/register">
                  <motion.button
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className={cn(
                      "group px-8 py-4 rounded-2xl font-semibold text-base",
                      "bg-gradient-to-r from-teal-500 to-cyan-500",
                      "hover:from-teal-400 hover:to-cyan-400",
                      "shadow-xl shadow-cyan-500/25 hover:shadow-cyan-500/40",
                      "transition-all duration-300 flex items-center gap-3"
                    )}
                  >
                    Request Ops Pilot
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </motion.button>
                </Link>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={cn(
                    "px-8 py-4 rounded-2xl font-semibold text-base",
                    "bg-white/5 border border-white/10",
                    "hover:bg-white/10 hover:border-white/20",
                    "transition-all duration-300 flex items-center gap-3"
                  )}
                >
                  <Play className="w-5 h-5 text-teal-400" />
                  Watch 90-second demo
                </motion.button>
              </motion.div>

              {/* Subnote */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="text-sm text-slate-500 mt-4"
              >
                14-day guided Ops pilot. Acceptance based on fit and accountability.
              </motion.p>
            </div>

            {/* Right - Simplified Professional Interface Mock */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, x: 30 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="relative hidden lg:block"
            >
              <div className={cn(
                "relative p-6 rounded-2xl",
                "bg-gradient-to-b from-slate-900/90 to-slate-900/70",
                "border border-slate-700/50",
                "shadow-2xl"
              )}>
                {/* Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-700/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white">
                      <DryVoxLogo size="sm" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white">DryVox Review</div>
                      <div className="text-xs text-slate-500">Pre-send checkpoint</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-xs text-emerald-400">Ready</span>
                  </div>
                </div>

                {/* Mock Review Content */}
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Eye className="w-4 h-4 text-slate-400" />
                      <span className="text-xs text-slate-400 uppercase tracking-wide">Reviewing</span>
                    </div>
                    <p className="text-sm text-white">Demurrage Claim - MV Ocean Star</p>
                  </div>

                  <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-400" />
                      <span className="text-xs text-amber-400 uppercase tracking-wide">Flagged</span>
                    </div>
                    <ul className="space-y-1 text-sm text-amber-200/80">
                      <li>Time calculation shows 2.3 days discrepancy</li>
                      <li>NOR timestamp missing from calculation</li>
                    </ul>
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50 border border-slate-700/30">
                    <span className="text-sm text-slate-400">Confidence</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div className="w-[72%] h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full" />
                      </div>
                      <span className="text-sm font-medium text-amber-400">72%</span>
                    </div>
                  </div>

                  <button className="w-full py-3 rounded-xl bg-teal-500/20 border border-teal-500/30 text-teal-400 text-sm font-medium hover:bg-teal-500/30 transition-colors">
                    Acknowledge & Review
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ============================================ */}
        {/* THE MOMENT WE OWN - Story 13.2 */}
        {/* ============================================ */}
        <section className="py-24 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left - Visual */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-700/50">
                  {/* Mock Email */}
                  <div className="mb-4 pb-4 border-b border-slate-700/50">
                    <div className="text-xs text-slate-500 mb-1">To: charterer@shipping.com</div>
                    <div className="text-sm font-medium text-white">Re: Demurrage Claim - MV Pacific Trader</div>
                  </div>
                  <div className="text-sm text-slate-400 space-y-2 mb-6">
                    <p>Dear Sir,</p>
                    <p>Please find attached our demurrage claim for USD 127,500 covering the period from...</p>
                    <p className="text-slate-500">...</p>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-3">
                    <button className="px-4 py-2 rounded-lg bg-slate-700 text-slate-300 text-sm font-medium">
                      Send
                    </button>
                    <button className="px-4 py-2 rounded-lg bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-medium flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Check with DryVox
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* Right - Content */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <span className="text-sm font-semibold text-teal-400 uppercase tracking-wider">
                  The moment DryVox exists for
                </span>
                <h2 className="text-3xl lg:text-4xl font-display font-bold text-white mt-4">
                  The 30 seconds before you send something you can't undo.
                </h2>
                <p className="text-slate-400 mt-4 text-lg">
                  DryVox is designed for the critical moments when accuracy matters most. This includes:
                </p>
                <ul className="mt-6 space-y-3">
                  {moments.map((moment, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-teal-400 flex-shrink-0" />
                      <span className="text-slate-300">{moment}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* TRUST & SAFETY - Story 13.3 */}
        {/* ============================================ */}
        <section className="py-24 border-t border-white/5">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="text-sm font-semibold text-teal-400 uppercase tracking-wider">
                Trust & Safety
              </span>
              <h2 className="text-3xl lg:text-4xl font-display font-bold text-white mt-4">
                DryVox will never:
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-4">
              {neverDo.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-5 rounded-xl bg-rose-500/5 border border-rose-500/20"
                >
                  <div className="w-10 h-10 rounded-lg bg-rose-500/10 flex items-center justify-center flex-shrink-0">
                    <XCircle className="w-5 h-5 text-rose-400" />
                  </div>
                  <span className="text-slate-300">{item.text}</span>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 p-6 rounded-xl bg-teal-500/10 border border-teal-500/20 text-center"
            >
              <p className="text-teal-200">
                <span className="font-semibold">Every output shows a confidence level</span> and flags where verification is needed.
              </p>
              <p className="text-slate-400 text-sm mt-3">
                DryVox may occasionally miss things. Its role is to reduce exposure, not replace human judgment.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ============================================ */}
        {/* BUYER FOCUS - Story 13.4 */}
        {/* ============================================ */}
        <section className="py-24 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="text-sm font-semibold text-teal-400 uppercase tracking-wider">
                Governance
              </span>
              <h2 className="text-3xl lg:text-4xl font-display font-bold text-white mt-4">
                DryVox is purchased and governed by Ops Leadership.
              </h2>
              <p className="text-slate-400 mt-4 text-lg max-w-2xl mx-auto">
                Other team members benefit under this governance structure.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {beneficiaries.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="p-6 rounded-xl bg-slate-800/30 border border-slate-700/30 text-center"
                  >
                    <div className="w-12 h-12 rounded-xl bg-slate-700/50 flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-6 h-6 text-teal-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{item.role}</h3>
                    <p className="text-sm text-slate-400">{item.benefit}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* OPS-CENTRIC USE CASES - Story 13.5 */}
        {/* ============================================ */}
        <section className="py-24 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="text-sm font-semibold text-teal-400 uppercase tracking-wider">
                Use Cases
              </span>
              <h2 className="text-3xl lg:text-4xl font-display font-bold text-white mt-4">
                What DryVox Checks
              </h2>
              <p className="text-slate-400 mt-4 text-lg max-w-2xl mx-auto">
                Four critical operational checkpoints for dry bulk teams.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {useCases.map((useCase, index) => {
                const IconComponent = useCase.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="p-6 rounded-xl bg-slate-800/30 border border-slate-700/30 hover:border-slate-600/50 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <div className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0",
                        "bg-gradient-to-br",
                        useCase.color
                      )}>
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-2">{useCase.title}</h3>
                        <p className="text-sm text-slate-400 leading-relaxed">{useCase.description}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* When NOT to use DryVox */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 p-6 rounded-xl bg-slate-800/50 border border-slate-700/30"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-slate-700/50 flex items-center justify-center flex-shrink-0">
                  <XCircle className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">When not to use DryVox</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    If the task is routine or low-risk, don't use DryVox.
                  </p>
                  <p className="text-slate-500 text-sm mt-2">
                    DryVox is for dispute-sensitive, irreversible moments only.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ============================================ */}
        {/* OUTPUT ARTIFACT PREVIEW - Story 13.6 */}
        {/* ============================================ */}
        <section className="py-24 border-t border-white/5">
          <div className="max-w-5xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="text-sm font-semibold text-teal-400 uppercase tracking-wider">
                What You'll See
              </span>
              <h2 className="text-3xl lg:text-4xl font-display font-bold text-white mt-4">
                Sample Review Output
              </h2>
              <p className="text-slate-400 mt-4 text-lg">
                Every DryVox review provides clear, actionable information.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl border border-slate-700/50 overflow-hidden bg-slate-900/80"
            >
              {/* Header */}
              <div className="px-6 py-4 bg-slate-800/50 border-b border-slate-700/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white">
                    <DryVoxLogo size="sm" />
                  </div>
                  <span className="font-semibold text-white">DryVox Review Summary</span>
                </div>
                <span className="text-xs text-slate-500 font-mono">Jan 13, 2026 • 14:32 UTC</span>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Reviewed */}
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">Reviewed</div>
                  <p className="text-white font-medium">Demurrage Claim - MV Ocean Star • USD 127,500</p>
                </div>

                {/* Flagged Items */}
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide mb-3">Flagged Items</div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                      <span className="text-sm text-amber-200">Time calculation shows 2.3 days discrepancy</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                      <span className="text-sm text-amber-200">NOR timestamp missing from calculation</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      <span className="text-sm text-emerald-200">Demurrage rate matches charter party</span>
                    </div>
                  </div>
                </div>

                {/* Confidence */}
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">Confidence Level</div>
                  <div className="flex items-center gap-4">
                    <div className="flex-1 h-3 bg-slate-700 rounded-full overflow-hidden">
                      <div className="w-[72%] h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full" />
                    </div>
                    <span className="text-lg font-bold text-amber-400">72%</span>
                    <span className="text-sm text-slate-500">(Medium)</span>
                  </div>
                </div>

                {/* Verification Notes */}
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">Verification Needed</div>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-slate-300">
                      <ArrowRight className="w-4 h-4 text-teal-400" />
                      Cross-check laytime with signed SOF
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-300">
                      <ArrowRight className="w-4 h-4 text-teal-400" />
                      Confirm NOR acceptance time with agent
                    </li>
                  </ul>
                </div>

                {/* Acknowledgement */}
                <div className="pt-4 border-t border-slate-700/50">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div className="w-5 h-5 rounded border-2 border-slate-600 group-hover:border-teal-500 transition-colors flex items-center justify-center">
                    </div>
                    <span className="text-sm text-slate-400">I have reviewed and verified these items</span>
                  </label>
                  <button className="mt-4 w-full py-3 rounded-xl bg-teal-500/20 border border-teal-500/30 text-teal-400 font-medium hover:bg-teal-500/30 transition-colors flex items-center justify-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Acknowledge & Proceed
                  </button>
                </div>
              </div>
            </motion.div>

            {/* Confidence Level Legend */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 p-6 rounded-xl bg-slate-800/30 border border-slate-700/30"
            >
              <h3 className="text-base font-semibold text-white mb-4">How confidence levels work</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 mt-2 flex-shrink-0" />
                  <div>
                    <span className="text-sm font-medium text-emerald-400">High</span>
                    <p className="text-xs text-slate-400 mt-0.5">Direct clause match or calculation with sufficient data</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
                  <div>
                    <span className="text-sm font-medium text-amber-400">Medium</span>
                    <p className="text-xs text-slate-400 mt-0.5">Interpretation required, verification recommended</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-orange-400 mt-2 flex-shrink-0" />
                  <div>
                    <span className="text-sm font-medium text-orange-400">Low</span>
                    <p className="text-xs text-slate-400 mt-0.5">Insufficient or incomplete information</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-rose-400 mt-2 flex-shrink-0" />
                  <div>
                    <span className="text-sm font-medium text-rose-400">Refuse</span>
                    <p className="text-xs text-slate-400 mt-0.5">Cannot answer without missing inputs</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ============================================ */}
        {/* BOTTOM CTA - Story 13.7 */}
        {/* ============================================ */}
        <section className="py-24 border-t border-white/5">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-4xl font-display font-bold text-white">
                Ready to add a checkpoint to your operations?
              </h2>
              <p className="text-slate-400 mt-4 text-lg max-w-2xl mx-auto">
                Request a guided pilot and see how DryVox helps your ops team catch issues before they become problems.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
                <Link to="/register">
                  <motion.button
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className={cn(
                      "group px-10 py-5 rounded-2xl font-semibold text-lg",
                      "bg-gradient-to-r from-teal-500 to-cyan-500",
                      "hover:from-teal-400 hover:to-cyan-400",
                      "shadow-xl shadow-cyan-500/25 hover:shadow-cyan-500/40",
                      "transition-all duration-300 inline-flex items-center gap-3"
                    )}
                  >
                    Request Ops Pilot
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </motion.button>
                </Link>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={cn(
                    "px-8 py-5 rounded-2xl font-semibold text-lg",
                    "bg-white/5 border border-white/10",
                    "hover:bg-white/10 hover:border-white/20",
                    "transition-all duration-300 flex items-center gap-3"
                  )}
                >
                  <MessageSquare className="w-5 h-5 text-teal-400" />
                  Contact Us
                </motion.button>
              </div>

              <p className="text-slate-500 text-sm mt-6">
                Pilots include weekly check-ins and success criteria review.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/5 py-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-white">
                <DryVoxLogo size="sm" />
              </div>
              <span className="text-xl font-display font-bold">
                <span className="text-white">Dry</span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-400">Vox</span>
              </span>
            </div>
            <div className="flex items-center gap-8 text-sm text-slate-500">
              <a href="#" className="hover:text-white transition-colors">Privacy</a>
              <a href="#" className="hover:text-white transition-colors">Terms</a>
              <a href="#" className="hover:text-white transition-colors">Contact</a>
            </div>
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} DryVox. Operational checkpoint for dry bulk ops.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default Landing;
